import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';

function flattenLessons(tree) {
  const flat = [];
  (tree || []).forEach((cat) => {
    (cat.mainTitles || []).forEach((mt) => {
      (mt.subTitles || []).forEach((st) => {
        flat.push({ cat, mt, st });
      });
    });
  });
  return flat;
}

// Converts lesson HTML into plain lowercase text once per lesson, so the
// search box can match against the actual written content, not just titles.
function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').toLowerCase();
}

function buildSearchIndex(flatLessons) {
  const index = new Map();
  flatLessons.forEach(({ st }) => {
    const text = [st.title, stripHtml(st.content?.en), stripHtml(st.content?.ta)]
      .join(' ')
      .toLowerCase();
    index.set(st.id, text);
  });
  return index;
}

function filterTree(tree, query, searchIndex) {
  const q = query.trim().toLowerCase();
  if (!q) return tree || [];
  return (tree || [])
    .map((cat) => {
      const catMatches = cat.name.toLowerCase().includes(q);
      const mainTitles = (cat.mainTitles || [])
        .map((mt) => {
          const mtMatches = mt.title.toLowerCase().includes(q);
          const subTitles = (mt.subTitles || []).filter((st) => {
            if (catMatches || mtMatches) return true;
            const indexed = searchIndex?.get(st.id);
            return indexed ? indexed.includes(q) : st.title.toLowerCase().includes(q);
          });
          return subTitles.length > 0 ? { ...mt, subTitles } : null;
        })
        .filter(Boolean);
      return mainTitles.length > 0 ? { ...cat, mainTitles } : null;
    })
    .filter(Boolean);
}

function LoadingSpinner() {
  return (
    <div className="flex h-screen flex-col items-center justify-center text-slate-400">
      <svg className="h-8 w-8 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
      </svg>
      <p className="mt-3 text-sm">Loading course…</p>
    </div>
  );
}

export default function LecturerSharedCourseViewer() {
  const { courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeLessonId, setActiveLessonId] = useState(null);
  const [expandedCatId, setExpandedCatId] = useState(null);
  const [search, setSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLang] = useState('en');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const articleRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await axiosInstance.get(`/lecturer/shared-courses/${courseId}`);
        if (cancelled) return;
        setCourse(data);
        const flat = flattenLessons(data.tree);
        if (flat.length > 0) {
          const lessonParam = searchParams.get('lesson');
          const match = lessonParam ? flat.find((l) => l.st.id === lessonParam) : null;
          const initial = match || flat[0];
          setActiveLessonId(initial.st.id);
          setExpandedCatId(initial.cat.id);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load this course.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Only re-run when the course itself changes — searchParams is read once
    // here for the initial deep link, not tracked afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const flatLessons = useMemo(() => flattenLessons(course?.tree), [course]);
  const activeIndex = useMemo(
    () => flatLessons.findIndex((l) => l.st.id === activeLessonId),
    [flatLessons, activeLessonId]
  );
  const active = activeIndex >= 0 ? flatLessons[activeIndex] : null;
  const prevLesson = activeIndex > 0 ? flatLessons[activeIndex - 1] : null;
  const nextLesson = activeIndex >= 0 && activeIndex < flatLessons.length - 1 ? flatLessons[activeIndex + 1] : null;

  const searchIndex = useMemo(() => buildSearchIndex(flatLessons), [flatLessons]);
  const visibleTree = useMemo(() => filterTree(course?.tree, search, searchIndex), [course, search, searchIndex]);

  const hasEnglish = Boolean(active?.st.content?.en?.trim());
  const hasTamil = Boolean(active?.st.content?.ta?.trim());
  const effectiveLang = lang === 'ta' && hasTamil ? 'ta' : hasEnglish ? 'en' : (hasTamil ? 'ta' : 'en');

  const selectLesson = (catId, lessonId) => {
    setActiveLessonId(lessonId);
    setExpandedCatId(catId);
    setSidebarOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('lesson', lessonId);
      return next;
    }, { replace: true });
    articleRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticleScroll = (e) => setShowScrollTop(e.target.scrollTop > 400);
  const scrollToTop = () => articleRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  // "/" focuses the course search box from anywhere on the page (unless
  // already typing in another field), matching the shortcut hint shown in
  // its placeholder.
  useEffect(() => {
    const handleSlash = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener('keydown', handleSlash);
    return () => document.removeEventListener('keydown', handleSlash);
  }, []);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center px-6 text-center text-sm text-slate-500">{error}</div>
    );
  }

  if (!course || flatLessons.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center px-6 text-center text-sm text-slate-500">
        This course doesn't have any published lessons yet.
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen w-80 shrink-0 flex-col border-r border-slate-200 bg-white transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="border-b border-slate-100 px-4 py-4">
          <div className="line-clamp-2 text-sm font-bold leading-snug text-slate-800">{course.title}</div>
          <div className="mt-1.5 text-xs text-slate-500">
            {flatLessons.length} {flatLessons.length === 1 ? 'lesson' : 'lessons'}
          </div>
        </div>

        <div className="relative px-4 pb-1 pt-3">
          <svg className="pointer-events-none absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entire course… ( / )"
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-8 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
          />
          {!search && (
            <kbd className="pointer-events-none absolute right-6 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-400">
              /
            </kbd>
          )}
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-6 pt-2">
          {visibleTree.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-slate-400">No lessons match your search.</p>
          ) : (
            visibleTree.map((cat) => {
              const expanded = search.trim() ? true : expandedCatId === cat.id;
              return (
                <div key={cat.id} className="mb-1">
                  <button
                    onClick={() => setExpandedCatId(expanded ? null : cat.id)}
                    className="flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left text-xs font-bold uppercase tracking-wide text-emerald-700 hover:bg-emerald-50 transition"
                  >
                    <svg className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02z" clipRule="evenodd" />
                    </svg>
                    <span className="min-w-0 flex-1 truncate">{cat.name}</span>
                  </button>

                  {expanded && cat.mainTitles.map((mt) => (
                    <div key={mt.id}>
                      <div className="mb-0.5 ml-7 mt-1.5 truncate pr-2 text-xs font-bold text-slate-600">{mt.title}</div>
                      {mt.subTitles.map((st) => {
                        const isActive = st.id === activeLessonId;
                        return (
                          <button
                            key={st.id}
                            onClick={() => selectLesson(cat.id, st.id)}
                            className={`mb-0.5 flex w-full items-center gap-2 rounded-lg py-2 pl-9 pr-3 text-left text-xs transition ${
                              isActive ? 'bg-emerald-50 font-semibold text-emerald-800' : 'text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            <span className="min-w-0 flex-1 truncate">{st.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            title="Table of contents"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 lg:hidden"
          >
            <svg className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>

          <div className="min-w-0 flex-1 truncate text-xs text-slate-500">
            {active ? `${active.cat.name} / ${active.mt.title} / ${active.st.title}` : course.title}
          </div>

          {(hasEnglish && hasTamil) && (
            <div className="flex shrink-0 gap-0.5 rounded-lg bg-slate-100 p-1">
              <button
                onClick={() => setLang('en')}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${effectiveLang === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('ta')}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${effectiveLang === 'ta' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                TA
              </button>
            </div>
          )}

          <span className="shrink-0 whitespace-nowrap rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            {activeIndex + 1} / {flatLessons.length}
          </span>

          <button
            onClick={() => window.close()}
            title="Close tab"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div ref={articleRef} onScroll={handleArticleScroll} className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full px-6 py-9 sm:px-10">
            {active && (
              <>
                {active.st.content?.[effectiveLang]?.trim() ? (
                  <div
                    className="lecturer-article-content text-[15.5px] leading-[1.8] text-slate-800"
                    dangerouslySetInnerHTML={{ __html: active.st.content[effectiveLang] }}
                  />
                ) : (
                  <p className="text-sm italic text-slate-400">Content for this lesson hasn't been added yet.</p>
                )}

                <div className="mt-12 flex items-start justify-between gap-3 border-t border-slate-200 pt-5">
                  <button
                    disabled={!prevLesson}
                    onClick={() => prevLesson && selectLesson(prevLesson.cat.id, prevLesson.st.id)}
                    className="flex max-w-[48%] items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:border-emerald-300 hover:text-emerald-700 transition disabled:pointer-events-none disabled:opacity-30"
                  >
                    <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                    </svg>
                    <span className="truncate">{prevLesson ? prevLesson.st.title : 'No previous lesson'}</span>
                  </button>
                  <button
                    disabled={!nextLesson}
                    onClick={() => nextLesson && selectLesson(nextLesson.cat.id, nextLesson.st.id)}
                    className="ml-auto flex max-w-[48%] items-center justify-end gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:border-emerald-300 hover:text-emerald-700 transition disabled:pointer-events-none disabled:opacity-30"
                  >
                    <span className="truncate">{nextLesson ? nextLesson.st.title : 'No next lesson'}</span>
                    <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <button
          onClick={scrollToTop}
          title="Back to top"
          className={`fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-600/30 transition ${
            showScrollTop ? 'opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
          }`}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      </main>

      <style>{`
        .lecturer-article-content h1 { font-size: 26px; font-weight: 700; margin: 28px 0 12px; color: #111827; }
        .lecturer-article-content h2 { font-size: 22px; font-weight: 700; margin: 24px 0 10px; color: #111827; }
        .lecturer-article-content h3 { font-size: 19px; font-weight: 700; margin: 20px 0 8px; color: #111827; }
        .lecturer-article-content h4 { font-size: 17px; font-weight: 700; margin: 16px 0 6px; color: #111827; }
        .lecturer-article-content p { margin: 0 0 16px; }
        .lecturer-article-content ul { list-style: disc; margin: 0 0 16px; padding-left: 24px; }
        .lecturer-article-content ol { list-style: decimal; margin: 0 0 16px; padding-left: 24px; }
        .lecturer-article-content li { margin-bottom: 6px; }
        .lecturer-article-content a { color: #059669; text-decoration: underline; }
        .lecturer-article-content blockquote {
          border-left: 4px solid #10b981;
          background: #ecfdf5;
          color: #065f46;
          padding: 10px 18px;
          margin: 16px 0;
          border-radius: 0 10px 10px 0;
        }
        .lecturer-article-content pre {
          background: #0f172a;
          color: #e2e8f0;
          padding: 16px;
          border-radius: 12px;
          overflow-x: auto;
          font-size: 13.5px;
          margin: 16px 0;
        }
        .lecturer-article-content code { font-family: 'SFMono-Regular', Consolas, monospace; }
        .lecturer-article-content img { max-width: 100%; border-radius: 12px; margin: 16px 0; }
        .lecturer-article-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 28px 0; }
        .lecturer-article-content table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
        .lecturer-article-content td, .lecturer-article-content th { border: 1px solid #e5e7eb; padding: 8px 10px; }
        .lecturer-article-content iframe { max-width: 100%; border-radius: 12px; }
      `}</style>
    </div>
  );
}
